import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { RichEditor, RichContent } from '../components/RichEditor';
import { ImportModal } from '../components/ImportModal';
import ExamSettingsModal from '../components/ExamSettingsModal';
import ExamChecklist from '../components/ExamChecklist';
import { useConfirm } from '../components/ConfirmDialog';
import Tooltip from '../components/Tooltip';

const EMPTY_EXAM = {
  title:'', description:'', instructions:'', duration_minutes:60,
  total_marks:100, passing_marks:40, proctoring_enabled:true,
  max_warnings:3, shuffle_questions:true, is_published:false,
  min_submit_minutes:30, require_fullscreen:true,
  start_time:'', end_time:'', disabled_violations:[],
};

// All violation types the AI/browser proctoring can detect.
// Unchecked = student still sees a warning toast, but it does NOT count toward
// total_warnings / trust_score / auto-termination.
const VIOLATION_TYPES = [
  { group: '📷 Camera / AI Detection', items: [
    { id: 'face_not_detected',  label: 'Face Not Detected',        hint: 'Not visible / camera covered / too far away' },
    { id: 'multiple_faces',     label: 'Multiple Faces',           hint: 'More than one person in frame' },
    { id: 'looking_away',       label: 'Looking Away',             hint: 'Not facing the camera consistently' },
    { id: 'suspicious_movement',label: 'Suspicious Movement',      hint: 'Unusual high motion detected' },
  ]},
  { group: '🖥️ Browser / Screen', items: [
    { id: 'tab_switch',         label: 'Tab Switch / Minimize',    hint: 'Switched tabs or minimized browser' },
    { id: 'window_blur',        label: 'Window Lost Focus',        hint: 'Clicked outside the exam window' },
    { id: 'full_screen_exit',   label: 'Fullscreen Exit',          hint: 'Exited fullscreen mode' },
    { id: 'window_focus_no_fullscreen', label: 'Returned Without Fullscreen', hint: 'Back but not in fullscreen' },
    { id: 'copy_paste',         label: 'Copy / Paste',             hint: 'Copy or paste attempted' },
    { id: 'right_click',        label: 'Right-Click',              hint: 'Right-click menu attempted' },
    { id: 'keyboard_shortcut',  label: 'Suspicious Shortcut',      hint: 'Dev tools / print-screen / alt-tab' },
  ]},
];

const EMPTY_Q = {
  question_text:'', question_type:'mcq',
  options:['','','',''], option_images:['','','',''],
  correct_answer:'', marks:2, explanation:'',
};

// ── Bulk Operations Modal ────────────────────────────────────────────────────
function BulkOperationsModal({ exam, onClose, onDone }) {
  const [op,      setOp]      = React.useState('');  // selected operation
  const [value,   setValue]   = React.useState(5);   // minutes
  const [loading, setLoading] = React.useState(false);
  const [result,  setResult]  = React.useState(null);

  const OPERATIONS = [
    { key:'resume_all_terminated', label:'▶ Resume All Terminated', desc:'Restart all terminated exams with remaining time', color:'var(--accent-success)', variant:'warning', impact:['All terminated students will be re-admitted','Their previously saved answers will be restored','Proctoring trust score resets to 60%','Students will receive a real-time resume notification'] },
    { key:'grant_time',            label:'⏱ Grant Extra Time',      desc:'Add minutes to all in-progress attempts',           color:'var(--accent-primary)', variant:'info',    impact:['Applies to all currently in-progress students','Each student gets the specified extra minutes','Students see a real-time timer update'] },
    { key:'deduct_time',           label:'⏳ Deduct Time',           desc:'Remove minutes from all in-progress attempts',      color:'var(--accent-warning)', variant:'warning', impact:['Applies to all currently in-progress students','Minimum remaining time will be 1 minute','Students see a real-time timer update'] },
    { key:'terminate_all',         label:'⛔ Terminate All',         desc:'Force-terminate all in-progress sessions',          color:'var(--accent-danger)',  variant:'danger',  impact:['All in-progress exams will stop immediately','Saved answers are preserved','Students see a termination notification','⚠ This cannot be undone'] },
  ];

  const selected = OPERATIONS.find(o => o.key === op);

  const execute = async () => {
    if (!op) return;
    setLoading(true);
    try {
      const r = await axios.post('/api/attempts/bulk', {
        exam_id: exam.id,
        operation: op,
        value: value,
      });
      setResult(r.data);
    } catch (err) {
      import('react-toastify').then(({ toast }) => toast.error(err.response?.data?.error || 'Operation failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:520 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <div>
            <h3 className="modal-title" style={{ margin:0 }}>⚙ Bulk Operations</h3>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:3 }}>{exam.title}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
        </div>

        {result ? (
          <div>
            <div style={{ padding:'20px', background:'rgba(16,185,129,0.1)', borderRadius:10, border:'1px solid rgba(16,185,129,0.3)', textAlign:'center', marginBottom:16 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>✅</div>
              <div style={{ fontWeight:700, fontSize:18, color:'var(--accent-success)', marginBottom:4 }}>Operation Complete</div>
              <div style={{ fontSize:14, color:'var(--text-secondary)' }}><strong>{result.affected}</strong> student{result.affected!==1?'s':''} affected</div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              <button className="btn btn-primary" onClick={() => { setResult(null); setOp(''); }}>Another Operation</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
              {OPERATIONS.map(o => (
                <div key={o.key} onClick={() => setOp(o.key)} style={{
                  padding:'12px 16px', borderRadius:10, cursor:'pointer',
                  border:`2px solid ${op===o.key ? o.color : 'var(--border)'}`,
                  background: op===o.key ? `${o.color}12` : 'var(--bg-elevated)',
                  transition:'all 0.15s',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:20, height:20, borderRadius:10, border:`2px solid ${op===o.key?o.color:'var(--border)'}`, background:op===o.key?o.color:'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {op===o.key && <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>✓</span>}
                    </div>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13, color:o.color }}>{o.label}</div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:1 }}>{o.desc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Value input for time operations */}
            {(op==='grant_time'||op==='deduct_time') && (
              <div className="form-group">
                <label className="form-label">{op==='grant_time'?'Minutes to add':'Minutes to deduct'}</label>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input className="form-input" type="number" min="1" max="120" value={value}
                    onChange={e => setValue(parseInt(e.target.value)||1)} style={{ width:100 }} />
                  <span style={{ fontSize:13, color:'var(--text-muted)' }}>minutes</span>
                </div>
              </div>
            )}

            {/* Impact preview */}
            {selected && (
              <div style={{ marginBottom:16, padding:'12px 14px', background:`${selected.color}10`, border:`1px solid ${selected.color}30`, borderRadius:8 }}>
                <div style={{ fontSize:11, fontWeight:700, color:selected.color, textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:8 }}>⚠ What will happen</div>
                {selected.impact.map((item, i) => (
                  <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom: i<selected.impact.length-1?5:0 }}>
                    <span style={{ color:selected.color, flexShrink:0 }}>•</span>
                    <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!op || loading} onClick={execute}>
                {loading ? '⏳ Running...' : selected ? `Execute: ${selected.label}` : 'Select an operation'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Clone exam form component ────────────────────────────────────────────────
function CloneExamForm({ source, onSubmit, onCancel }) {
  const [form, setForm] = React.useState({
    title: `${source.title} (Copy)`,
    start_time: '',
    end_time: '',
  });
  const [saving, setSaving] = React.useState(false);
  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        title: form.title,
        start_time: form.start_time ? istInputToUTC(form.start_time) : null,
        end_time:   form.end_time   ? istInputToUTC(form.end_time)   : null,
      });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit}>
      <div className="form-group">
        <label className="form-label">New Exam Title *</label>
        <input className="form-input" value={form.title} onChange={f('title')} required />
      </div>
      <div className="form-group">
        <label className="form-label">📅 Start Date & Time <span style={{ fontSize:11, color:'var(--text-muted)' }}>(optional)</span></label>
        <input className="form-input" type="datetime-local"
          min={nowLocalInput()}
          value={form.start_time} onChange={f('start_time')} />
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Students cannot start the exam before this date & time</div>
      </div>
      <div className="form-group">
        <label className="form-label">⏰ End Date & Time <span style={{ fontSize:11, color:'var(--text-muted)' }}>(optional)</span></label>
        <input className="form-input" type="datetime-local"
          min={nowLocalInput()}
          value={form.end_time} onChange={f('end_time')} />
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Students cannot start the exam after this date & time</div>
      </div>
      <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Cloning...' : `⎘ Clone Exam (${source.question_count} questions)`}
        </button>
      </div>
    </form>
  );
}

// ── Date/time helpers ─────────────────────────────────────────────────────────
// Convert any date string/object to LOCAL datetime-local input format (YYYY-MM-DDTHH:MM)
// datetime-local inputs must receive LOCAL time, NOT UTC ISO strings
// ── IST (Asia/Kolkata, UTC+5:30) date/time helpers ─────────────────────────────
// CRITICAL: This platform is India-only. We do NOT rely on the browser's or
// server's OS timezone for exam start/end times — that caused times to silently
// shift to "server time" (often UTC) whenever the app ran on a machine whose
// system clock wasn't set to IST. All exam scheduling is always interpreted
// and displayed as IST, regardless of where the browser/server physically runs.
const IST_TZ = 'Asia/Kolkata';
const IST_OFFSET_MS = (5 * 60 + 30) * 60000; // India has no DST — fixed offset is safe

function getISTParts(dateOrIso) {
  const d = typeof dateOrIso === 'string' ? new Date(dateOrIso) : dateOrIso;
  if (!d || isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const parts = {};
  fmt.formatToParts(d).forEach(p => { parts[p.type] = p.value; });
  return parts;
}

// UTC ISO string (from backend) → "YYYY-MM-DDTHH:mm" IST wall-clock for datetime-local value=
const toLocalInput = (isoUTC) => {
  if (!isoUTC) return '';
  const p = getISTParts(isoUTC);
  if (!p) return '';
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// "now" expressed in IST, for the min= attribute on date pickers
const nowLocalInput = () => toLocalInput(new Date().toISOString());

// datetime-local's "YYYY-MM-DDTHH:mm" (entered by the user AS IST) → correct UTC ISO string
const istInputToUTC = (localValue) => {
  if (!localValue) return null;
  const [datePart, timePart] = localValue.split('T');
  const [y, mo, da] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const utcMs = Date.UTC(y, mo - 1, da, h, mi) - IST_OFFSET_MS;
  return new Date(utcMs).toISOString();
};

// Friendly read-only IST display, e.g. "25 Jun 2026, 02:30 PM IST"
const formatIST = (isoUTC, dateOnly = false) => {
  if (!isoUTC) return '';
  const d = new Date(isoUTC);
  if (isNaN(d.getTime())) return '';
  if (dateOnly) {
    return d.toLocaleDateString('en-IN', { timeZone: IST_TZ, day: '2-digit', month: 'short', year: 'numeric' });
  }
  return d.toLocaleString('en-IN', { timeZone: IST_TZ, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' IST';
};

export default function ManageExams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const isAdmin       = user?.role === 'admin';
  const isInstructor  = user?.role === 'instructor';
  const isOrg         = user?.role === 'organization';
  const canCreateExam = isOrg;                        // only org can create exams
  const canEdit       = isOrg;                        // only org can edit/delete/publish
  const canOperate    = isOrg || isInstructor;        // both can monitor/open-to-students

  const [exams,         setExams]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showQModal,    setShowQModal]    = useState(false);
  const [editExam,      setEditExam]      = useState(EMPTY_EXAM);
  const [editQ,         setEditQ]         = useState(EMPTY_Q);
  const [selectedExam,  setSelectedExam]  = useState(null);
  const [leftTab,       setLeftTab]       = useState('questions'); // 'questions' | 'proctors'
  const [selectedQIds,  setSelectedQIds]  = useState(new Set()); // bulk question selection
  const [proctorList,   setProctorList]   = useState(null); // instructors for current exam
  const [questions,     setQuestions]     = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [previewQ,      setPreviewQ]      = useState(null);  // full question preview
  const [showQImport,   setShowQImport]   = useState(false); // question import modal
  const [settingsExam,  setSettingsExam]  = useState(null);  // exam settings modal
  const [checklistExam, setChecklistExam] = useState(null); // active checklist exam
  const [checklistStats, setChecklistStats] = useState({}); // {examId: {questions,students,instructors}}
  const checklistRef = React.useRef({});
  const [showBulkModal, setShowBulkModal] = useState(null);  // bulk operations modal
  const [attendance,    setAttendance]    = useState(null);  // attendance panel data
  const [showAttend,    setShowAttend]    = useState(false); // show attendance modal
  const optImageRefs   = useRef([]);

  // ── Data fetching ────────────────────────────────────────────────────────────
  const _loadChecklistStats = async (examId) => {
    try {
      const [qRes, enRes, instrRes] = await Promise.all([
        axios.get(`/api/exams/${examId}/questions`).catch(() => ({ data: [] })),
        axios.get(`/api/exams/${examId}/enrollment-count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`/api/exams/${examId}/instructors`).catch(() => ({ data: [] })),
      ]);
      const stats = {
        questions:   Array.isArray(qRes.data) ? qRes.data.length : 0,
        students:    enRes.data?.count ?? 0,
        instructors: Array.isArray(instrRes.data) ? instrRes.data.length : 0,
      };
      setChecklistStats(prev => ({ ...prev, [examId]: stats }));
    } catch {}
  };

  const fetchExams = () => {
    axios.get('/api/exams/manage')
      .then(r => setExams(r.data))
      .catch(() => toast.error('Failed to load exams'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchExams(); }, []);

  const handleImportQuestions = async (rows) => {
    const res = await axios.post(`/api/exams/${selectedExam.id}/import/questions`, { rows });
    fetchQuestions(selectedExam);
    return res.data;
  };

  const fetchAttendance = async (exam) => {
    try {
      const res = await axios.get(`/api/exams/${exam.id}/attendance`);
      setAttendance({ ...res.data, exam });
      setShowAttend(true);
    } catch { toast.error('Failed to load attendance'); }
  };

  const fetchProctors = async (exam) => {
    setProctorList(null);
    try {
      const r = await axios.get(`/api/exams/${exam.id}/instructors`);
      setProctorList(r.data || []);
    } catch { setProctorList([]); }
  };

  const fetchQuestions = (exam) => {
    setSelectedExam(exam);
    setChecklistExam(exam);
    setQuestions([]);
    fetchProctors(exam); // load proctors too
    _loadChecklistStats(exam.id); // refresh checklist when exam is selected
    axios.get(`/api/exams/${exam.id}/questions`)
      .then(r => setQuestions(r.data))
      .catch(err => {
        if (err.response?.status === 403) {
          setQuestions(null); // null = hidden or permission denied by org
        } else {
          toast.error('Failed to load questions');
        }
      });
  };

  // ── Save exam ────────────────────────────────────────────────────────────────
  const handleSaveExam = async () => {
    if (!editExam.title) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (editExam.id) {
        await axios.put(`/api/exams/${editExam.id}`, editExam);
        toast.success('Exam updated');
      } else {
        await axios.post('/api/exams', editExam);
        toast.success('Exam created');
      }
      setShowExamModal(false);
      setEditExam(EMPTY_EXAM);
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save exam');
    } finally { setSaving(false); }
  };

  // ── Save question ─────────────────────────────────────────────────────────────
  const handleSaveQuestion = async () => {
    if (!editQ.question_text?.trim()) { toast.error('Question text required'); return; }
    if (!selectedExam) return;
    setSaving(true);
    try {
      const payload = { ...editQ, order_index: questions.length };
      if (editQ.question_type === 'mcq') {
        // Combine text + optional image into options array (store as JSON)
        payload.options = editQ.options.filter(o => o.trim());
        // Store option_images alongside options in explanation JSON field if any set
        const hasImages = editQ.option_images?.some(img => img);
        if (hasImages) {
          payload.explanation = JSON.stringify({
            text:    editQ.explanation,
            option_images: editQ.option_images,
          });
        }
      }
      await axios.post(`/api/exams/${selectedExam.id}/questions`, payload);
      toast.success('Question added');
      setShowQModal(false);
      setEditQ(EMPTY_Q);
      fetchQuestions(selectedExam);
    } catch {
      toast.error('Failed to save question');
    } finally { setSaving(false); }
  };

  const handleDeleteExam = async (exam) => {
    const id = typeof exam === 'string' ? exam : exam?.id;
    const ok = await confirm({
      title: 'Delete Exam?',
      message: `"${exam?.title || 'This exam'}" will be permanently removed.`,
      impact: [
        'All questions and answer options will be deleted',
        `${exam?.enrolled_count || 0} enrolled students will lose access`,
        `${exam?.attempt_count || 0} existing attempts, scores and answers will be deleted`,
        'All proctoring events and snapshots will be deleted',
        'Registration links for this exam will be deactivated',
        '⛔ This action cannot be undone',
      ],
      confirmLabel: 'Delete Exam',
      variant: 'danger',
      requirePassword: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/exams/${id}`);
      toast.success('Exam deleted');
      if (selectedExam?.id === id) setSelectedExam(null);
      fetchExams();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  };

  const handleDeleteQ = async (qId) => {
    const ok = await confirm({ title:'Delete Question?', impact:['This question and all student answers for it will be permanently deleted'], confirmLabel:'Delete Question', variant:'danger' });
    if (!ok) return;
    await axios.delete(`/api/exams/${selectedExam.id}/questions/${qId}`);
    toast.success('Question deleted');
    fetchQuestions(selectedExam);
  };

  const handleDeleteSelectedQs = async () => {
    if (selectedQIds.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selectedQIds.size} Question${selectedQIds.size > 1 ? 's' : ''}?`,
      impact: [`${selectedQIds.size} questions and all related student answers will be permanently deleted`],
      confirmLabel: `Delete ${selectedQIds.size} Questions`, variant: 'danger',
    });
    if (!ok) return;
    await Promise.all([...selectedQIds].map(id =>
      axios.delete(`/api/exams/${selectedExam.id}/questions/${id}`).catch(() => {})
    ));
    toast.success(`${selectedQIds.size} questions deleted`);
    setSelectedQIds(new Set());
    fetchQuestions(selectedExam);
  };

  // Only Org can open/close exam. Password passed in from the button handler above.
  const handleProctorPublish = async (exam, publish, org_password = null) => {
    // Gate: always fetch fresh before opening to students
    if (publish) {
      const [qRes, enRes] = await Promise.all([
        axios.get(`/api/exams/${exam.id}/questions`).catch(() => ({ data: [] })),
        axios.get(`/api/exams/${exam.id}/enrollment-count`).catch(() => ({ data: { count: 0 } })),
      ]);
      const qCount = Array.isArray(qRes.data) ? qRes.data.length : 0;
      const sCount = enRes.data?.count || 0;
      setChecklistStats(prev => ({ ...prev, [exam.id]: { ...prev[exam.id], questions: qCount, students: sCount } }));
      const missing = [];
      if (!exam.title?.trim()) missing.push('Exam title/details missing');
      if (qCount < 1) missing.push('No questions added yet');
      if (sCount < 1) missing.push('No students enrolled yet (map a group or use registration link)');
      if (missing.length > 0) {
        toast.error(
          <div>
            <div style={{ fontWeight:700, marginBottom:6 }}>⛔ Cannot open — checklist incomplete</div>
            {missing.map((m,i) => <div key={i} style={{ fontSize:13 }}>• {m}</div>)}
          </div>,
          { autoClose: 8000 }
        );
        return;
      }
    }
    try {
      await axios.post(`/api/exams/${exam.id}/proctor-publish`, { publish, org_password });
      toast.success(publish ? '🟢 Exam opened to students!' : '🔴 Exam closed to students');
      fetchExams();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
      if (err.response?.data?.requires_password) {
        toast.info('Enter the correct exam password to open this exam.', { autoClose: 8000 });
      }
    }
  };

  // ── End exam permanently ─────────────────────────────────────────────────────
  const handleEndExam = async (exam) => {
    const ok = await confirm({
      title: 'End Exam Permanently?',
      message: `"${exam.title}" will be officially ended. This cannot be undone.`,
      impact: [
        'Exam will be closed and unpublished immediately',
        'Students will no longer be able to start or resume this exam',
        `Any in-progress sessions will be auto-terminated`,
        'Registration links will be deactivated',
        'Attendance & results remain accessible for review',
        '⚠ This action is irreversible',
      ],
      confirmLabel: 'End Exam Permanently',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const r = await axios.post(`/api/exams/${exam.id}/end`);
      toast.success(`Exam ended. ${r.data.terminated_attempts || 0} in-progress session(s) terminated.`);
      fetchExams();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  // ── Clone exam ────────────────────────────────────────────────────────────────
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSource,    setCloneSource]    = useState(null);

  const handleClone = async ({ title, start_time, end_time }) => {
    try {
      const r = await axios.post(`/api/exams/${cloneSource.id}/clone`, { title, start_time, end_time });
      toast.success(`Exam cloned! ${r.data.questions_cloned} questions copied.`);
      setShowCloneModal(false);
      setCloneSource(null);
      fetchExams();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to clone'); }
  };

  const handleVisibility = async (exam, visible) => {
    try {
      await axios.post(`/api/exams/${exam.id}/visibility`, { visible_to_proctor: visible });
      toast.success(visible ? '👁 Exam now visible to proctor' : '🔒 Exam hidden from proctor');
      fetchExams();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const togglePublish = async (exam) => {
    if (!exam.is_published) {
      // Always fetch fresh — never trust stale cache for gate checks
      const [qRes, enRes] = await Promise.all([
        axios.get(`/api/exams/${exam.id}/questions`).catch(() => ({ data: [] })),
        axios.get(`/api/exams/${exam.id}/enrollment-count`).catch(() => ({ data: { count: 0 } })),
      ]);
      const qCount = Array.isArray(qRes.data) ? qRes.data.length : 0;
      const sCount = enRes.data?.count || 0;
      // Update cache with fresh data
      setChecklistStats(prev => ({ ...prev, [exam.id]: { ...prev[exam.id], questions: qCount, students: sCount } }));
      const missing = [];
      if (!exam.title?.trim()) missing.push('Add exam title and details');
      if (qCount < 1) missing.push('Add at least 1 question');
      if (sCount < 1) missing.push('Enroll at least 1 student (map a group or create a registration link)');
      if (missing.length > 0) {
        toast.error(
          <div>
            <div style={{ fontWeight:700, marginBottom:6 }}>⛔ Checklist incomplete — cannot publish</div>
            {missing.map((m,i) => <div key={i} style={{ fontSize:13 }}>• {m}</div>)}
          </div>,
          { autoClose: 8000 }
        );
        return;
      }
    }
    await axios.put(`/api/exams/${exam.id}`, { ...exam, is_published: !exam.is_published });
    toast.success(exam.is_published ? 'Unpublished' : 'Published');
    fetchExams();
  };

  // ── Option image upload ───────────────────────────────────────────────────────
  const handleOptionImage = (i, file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error('Image must be under 1.5MB'); return; }
    const reader = new FileReader();
    reader.onload = e => {
      const imgs = [...(editQ.option_images || ['','','',''])];
      imgs[i] = e.target.result;
      setEditQ(p => ({ ...p, option_images: imgs }));
    };
    reader.readAsDataURL(file);
  };

  // ── Parse option_images from question ────────────────────────────────────────
  const getOptionImages = (q) => {
    try {
      if (!q.explanation) return [];
      const parsed = JSON.parse(q.explanation);
      return parsed.option_images || [];
    } catch { return []; }
  };
  const getExplanationText = (q) => {
    try {
      const parsed = JSON.parse(q.explanation);
      return typeof parsed === 'object' ? (parsed.text || '') : q.explanation;
    } catch { return q.explanation || ''; }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <>
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h1 className="page-title">Manage Exams</h1>
            <p className="page-subtitle">{isAdmin ? '📊 View-only — exams are managed by organizations' : 'Create, edit, and manage exam content'}</p>
          </div>
          {canCreateExam && <button className="btn btn-primary" onClick={() => navigate('/exams/create')}>+ New Exam</button>}
        </div>

        <div style={{ display:'grid', gridTemplateColumns: (checklistExam && isOrg) ? 'minmax(0,1fr) 290px' : '1fr', gap:16, alignItems:'start' }}>
          {/* ── Left: Exam list + Questions ───────────────────────────────── */}
          <div>
          <div style={{ display:'grid', gridTemplateColumns: selectedExam ? '340px minmax(0,1fr)' : '1fr', gap:16, alignItems:'start' }}>

          {/* ── Exam list ─────────────────────────────────────────────────── */}
          <div className="card">
            <h3 style={{ fontSize:15, fontWeight:600, marginBottom:16 }}>All Exams ({exams.length})</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {exams.length === 0 && <div style={{ textAlign:'center', color:'var(--text-secondary)', padding:24 }}>No exams yet.</div>}
              {exams.map(exam => (
                <div key={exam.id} onClick={() => { fetchQuestions(exam); if (isOrg) setChecklistExam(exam); }} style={{
                  padding:'14px 16px', cursor:'pointer', transition:'var(--transition)',
                  background: selectedExam?.id===exam.id ? 'rgba(79,142,247,0.1)' : 'var(--bg-elevated)',
                  borderRadius:'var(--radius-sm)',
                  border:`1px solid ${selectedExam?.id===exam.id ? 'var(--accent-primary)' : 'var(--border)'}`,
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <div style={{ fontWeight:500, fontSize:14, flex:1 }}>{exam.title}</div>
                    <div style={{ display:'flex', gap:5, marginLeft:8, flexWrap:'wrap' }}>
                      {exam.is_ended && <span className="badge badge-red" style={{ fontWeight:700 }}>🛑 ENDED</span>}
                      {/* Mini checklist indicator */}
                      {checklistStats[exam.id] && (() => {
                        const s = checklistStats[exam.id];
                        const reqDone = (exam.title?1:0) + (s.questions>=1?1:0) + (s.students>=1?1:0);
                        return reqDone < 3
                          ? <span style={{ fontSize:9, padding:'1px 6px', borderRadius:10, background:'rgba(239,68,68,0.15)', color:'#dc2626', fontWeight:800 }}>⛔ {reqDone}/3</span>
                          : <span style={{ fontSize:9, padding:'1px 6px', borderRadius:10, background:'rgba(16,185,129,0.15)', color:'#15803d', fontWeight:800 }}>✅</span>;
                      })()}
                      <span className={`badge badge-${exam.is_published?'green':'yellow'}`}>
                        {exam.is_published ? 'Org:Live' : 'Draft'}
                      </span>
                      {exam.is_published && (
                        <span className={`badge badge-${exam.proctor_published?'green':'red'}`}>
                          {exam.proctor_published ? '🟢 Open' : '🔴 Closed'}
                        </span>
                      )}
                      {exam.visible_to_proctor===false && <span className="badge badge-yellow">🔒 Hidden</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:10, fontSize:12, color:'var(--text-muted)', marginBottom:10, flexWrap:'wrap' }}>
                    <span>❓ {exam.question_count} Qs</span>
                    <span>⏱ {exam.duration_minutes}m</span>
                    <span>👥 {exam.attempt_count||0} attempts</span>
                    {exam.proctoring_enabled && <Tooltip text="AI proctoring is enabled"><span style={{ color:'var(--accent-warning)' }}>🔒 Proctored</span></Tooltip>}
                    {exam.start_time && <Tooltip text={`Students can start from ${formatIST(exam.start_time)}`}><span style={{ color:'var(--accent-primary)' }}>📅 {formatIST(exam.start_time, true)}</span></Tooltip>}
                    {exam.end_time && <Tooltip text={`Exam closes at ${formatIST(exam.end_time)}`}><span style={{ color:'var(--accent-danger)' }}>⏰ {formatIST(exam.end_time, true)}</span></Tooltip>}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:0 }}>

                    {/* ── PRE-EXAM ─────────────────────────────────────────── */}
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:9, fontWeight:800, color:'var(--accent-primary)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:5, display:'flex', alignItems:'center', gap:4 }}>
                        <span style={{ display:'inline-block', width:5, height:5, borderRadius:3, background:'var(--accent-primary)' }} />
                        Pre-Exam Setup
                      </div>
                      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                        {canEdit && (
                          <Tooltip text="Edit exam title, duration, instructions and settings">
                            <button className="btn btn-secondary btn-sm" onClick={e=>{ e.stopPropagation(); setEditExam({...exam, disabled_violations: exam.disabled_violations || []}); setShowExamModal(true); }}>✏️ Edit</button>
                          </Tooltip>
                        )}
                        {isOrg && (
                          <Tooltip text={exam.is_published ? 'Unpublish — remove from student dashboard' : 'Publish — students can see this exam'}>
                            <button className="btn btn-secondary btn-sm" onClick={async e=>{ e.stopPropagation();
                              const ok = await confirm({
                                title: exam.is_published ? 'Unpublish Exam?' : 'Publish Exam?',
                                message: `"${exam.title}"`,
                                impact: exam.is_published
                                  ? ['Students will no longer see this exam in their dashboard', 'Active registration links will stop working', 'Proctors cannot open it to students']
                                  : ['Enrolled students will see this exam', 'Registration links become active', 'Org can then open it to students via proctors'],
                                confirmLabel: exam.is_published ? 'Unpublish' : 'Publish',
                                variant: exam.is_published ? 'warning' : 'info',
                                requirePassword: true,
                              });
                              if (ok) togglePublish(exam);
                            }}>{exam.is_published ? 'Unpublish' : 'Publish'}</button>
                          </Tooltip>
                        )}
                        {isOrg && (
                          <Tooltip text={exam.visible_to_proctor===false ? 'Hidden from proctor — click to reveal' : 'Visible to proctor — click to hide'}>
                            <button className="btn btn-secondary btn-sm"
                              style={{ color: exam.visible_to_proctor===false ? 'var(--text-muted)' : 'var(--accent-success)' }}
                              onClick={async e=>{ e.stopPropagation();
                                const hiding = exam.visible_to_proctor !== false;
                                const ok = await confirm({
                                  title: hiding ? 'Hide Exam from Proctor?' : 'Make Exam Visible to Proctor?',
                                  impact: hiding
                                    ? ['Proctor will not see this exam', 'Proctor cannot open or monitor it']
                                    : ['Proctor can see and operate this exam', 'Proctor can open it to students'],
                                  confirmLabel: 'Confirm',
                                  variant: 'info',
                                  requirePassword: true,
                                });
                                if (ok) handleVisibility(exam, !exam.visible_to_proctor);
                              }}>
                              {exam.visible_to_proctor===false ? '🔒 Hidden' : '👁 Visible'}
                            </button>
                          </Tooltip>
                        )}
                        {isOrg && (
                          <Tooltip text="Exam settings — proctor permissions, grading logic, student download">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--text-secondary)' }} onClick={e=>{ e.stopPropagation(); setSettingsExam(exam); }}>⚙ Settings</button>
                          </Tooltip>
                        )}
                        {isOrg && (
                          <Tooltip text="Clone this exam with same questions — set new date/time">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-secondary)' }} onClick={e=>{ e.stopPropagation(); setCloneSource(exam); setShowCloneModal(true); }}>⎘ Clone</button>
                          </Tooltip>
                        )}

                      </div>
                    </div>

                    {/* ── DURING EXAM ──────────────────────────────────────── */}
                    {exam.is_published && !exam.is_ended && (
                      <div style={{ marginBottom:10, paddingTop:8, borderTop:'1px dashed var(--border)' }}>
                        <div style={{ fontSize:10, fontWeight:800, color:'var(--accent-warning)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
                          <span style={{ display:'inline-block', width:6, height:6, borderRadius:3, background:'var(--accent-warning)' }} />
                          During Exam
                        </div>
                        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                          <Tooltip text="Live monitor — see all students, camera feeds, violations in real time">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-primary)' }} onClick={e=>{ e.stopPropagation(); navigate(`/live/${exam.id}`); }}>📡 Live Monitor</button>
                          </Tooltip>
                          {canOperate && (
                            <Tooltip text="Pre-exam attendance — who logged in, who opened instructions">
                              <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-warning)' }} onClick={e=>{ e.stopPropagation(); fetchAttendance(exam); }}>👥 Attendance</button>
                            </Tooltip>
                          )}
                          {/* Open / Close to students */}
                          {isOrg && exam.visible_to_proctor !== false && (
                            <Tooltip text={exam.proctor_published ? 'Close exam — students cannot start new attempts' : 'Open exam — students can start (exam password required)'}>
                              <button className={`btn btn-sm ${exam.proctor_published ? 'btn-danger' : 'btn-success'}`}
                                onClick={async e=>{ e.stopPropagation();
                                  if (exam.proctor_published) {
                                    const ok = await confirm({
                                      title: 'Close Exam to Students?',
                                      message: `"${exam.title}"`,
                                      impact: ['New students cannot start the exam', 'Students currently inside the exam are not affected', 'You can re-open at any time'],
                                      confirmLabel: 'Close Exam',
                                      variant: 'warning',
                                      requirePassword: true,
                                    });
                                    if (ok) await handleProctorPublish(exam, false, null);
                                  } else {
                                    const entered = window.prompt(`🔑 Enter the exam start password to open "${exam.title}" to students:\n(Find it under ⚙ Settings → Password)`);
                                    if (!entered) return;
                                    await handleProctorPublish(exam, true, entered);
                                  }
                                }}>
                                {exam.proctor_published ? '🔴 Close Exam' : '🟢 Open to Students'}
                              </button>
                            </Tooltip>
                          )}
                          {isInstructor && exam.visible_to_proctor !== false && (
                            <Tooltip text={exam.proctor_published ? 'Close exam — students cannot start' : 'Open exam — enter password provided by organization'}>
                              <button className={`btn btn-sm ${exam.proctor_published ? 'btn-danger' : 'btn-success'}`}
                                onClick={async e=>{ e.stopPropagation();
                                  if (exam.proctor_published) {
                                    const ok = await confirm({
                                      title: 'Close Exam?',
                                      message: `"${exam.title}"`,
                                      impact: ['Students cannot start new exam attempts', 'In-progress sessions are unaffected'],
                                      confirmLabel: 'Close Exam',
                                      variant: 'warning',
                                    });
                                    if (ok) await handleProctorPublish(exam, false, null);
                                  } else {
                                    const entered = window.prompt(`🔑 Enter exam password (ask your organization):`);
                                    if (!entered) return;
                                    await handleProctorPublish(exam, true, entered);
                                  }
                                }}>
                                {exam.proctor_published ? '🔴 Close Exam' : '🟢 Open to Students'}
                              </button>
                            </Tooltip>
                          )}
                          {/* Bulk operations */}
                          {canOperate && (
                            <Tooltip text="Bulk — resume terminated, grant/deduct time for all students">
                              <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-secondary)' }} onClick={e=>{ e.stopPropagation(); setShowBulkModal(exam); }}>⚙ Bulk Ops</button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── POST EXAM ────────────────────────────────────────── */}
                    <div style={{ paddingTop:8, borderTop:'1px dashed var(--border)' }}>
                      <div style={{ fontSize:10, fontWeight:800, color:'var(--accent-success)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:6, display:'flex', alignItems:'center', gap:5 }}>
                        <span style={{ display:'inline-block', width:6, height:6, borderRadius:3, background:'var(--accent-success)' }} />
                        Reports & Close
                      </div>
                      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                        {canOperate && (
                          <Tooltip text="Full attendance & result report — export as PDF or Excel">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-secondary)' }} onClick={e=>{ e.stopPropagation(); navigate(`/reports/attendance/${exam.id}`); }}>📊 Report</button>
                          </Tooltip>
                        )}
                        {isOrg && (
                          <Tooltip text="Download all questions with correct answers (org only)">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-success)' }} onClick={e=>{ e.stopPropagation(); navigate(`/reports/qa/${exam.id}`); }}>📋 Q&A</button>
                          </Tooltip>
                        )}
                        {isOrg && !exam.is_ended && (
                          <Tooltip text="Permanently end this exam — students can never attempt it again">
                            <button className="btn btn-secondary btn-sm" style={{ color:'var(--accent-warning)' }}
                              onClick={async e=>{ e.stopPropagation();
                                const ok = await confirm({
                                  title: 'End Exam Permanently?',
                                  message: `"${exam.title}" will be officially and permanently closed.`,
                                  impact: [
                                    'Exam will be unpublished and closed immediately',
                                    'All students currently in the exam will be terminated',
                                    'No new attempts can ever be started',
                                    'Registration links will be deactivated',
                                    'Results and attendance remain accessible for download',
                                    '⛔ This action CANNOT be undone',
                                  ],
                                  confirmLabel: 'End Exam Permanently',
                                  variant: 'danger',
                                  requirePassword: true,
                                });
                                if (ok) handleEndExam(exam);
                              }}>🛑 End Exam</button>
                          </Tooltip>
                        )}
                        {exam.is_ended && (
                          <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background:'rgba(239,68,68,0.1)', color:'var(--accent-danger)', fontWeight:700 }}>🛑 Ended</span>
                        )}
                        {isOrg && !exam.is_ended && (
                          <Tooltip text="Permanently delete this exam — removes all questions, attempts and data">
                            <button className="btn btn-danger btn-sm"
                              onClick={async e=>{ e.stopPropagation(); handleDeleteExam(exam); }}>🗑 Delete</button>
                          </Tooltip>
                        )}
                      </div>
                    </div>

                    {isAdmin && <div style={{ marginTop:8, fontSize:11, color:'var(--text-muted)' }}>👁 Admin — view only</div>}
                    {isInstructor && !exam.is_published && <div style={{ marginTop:8, fontSize:11, color:'var(--accent-warning)' }}>⚠ Not published by organization yet</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Questions / Proctors panel (tabbed) ──────────────────────── */}
          {selectedExam && (
            <div className="card">
              {/* Tab header */}
              <div style={{ display:'flex', gap:6, marginBottom:14, paddingBottom:12, borderBottom:'1px solid var(--border)', alignItems:'center' }}>
                <button onClick={() => setLeftTab('questions')} className="btn btn-secondary btn-sm"
                  style={leftTab==='questions' ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
                  ❓ Questions {questions !== null ? `(${questions.length})` : '🔒'}
                </button>
                <button onClick={() => setLeftTab('proctors')} className="btn btn-secondary btn-sm"
                  style={leftTab==='proctors' ? { borderColor:'var(--accent-secondary)', color:'var(--accent-secondary)', background:'rgba(139,92,246,0.1)' } : {}}>
                  👨‍🏫 Proctors {proctorList ? `(${proctorList.length})` : ''}
                </button>
                <div style={{ flex:1 }} />
                <span style={{ fontSize:11, color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:160 }}>{selectedExam.title}</span>
              </div>

              {/* ── QUESTIONS TAB ─────────────────────────────────────── */}
              {leftTab === 'questions' && (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, fontWeight:600 }}>
                    {questions !== null && canEdit && (
                      <input type="checkbox" title="Select all"
                        checked={questions.length > 0 && selectedQIds.size === questions.length}
                        onChange={e => setSelectedQIds(e.target.checked ? new Set(questions.map(q => q.id)) : new Set())}
                        style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--accent-primary)' }} />
                    )}
                    {questions === null ? '🔒 Hidden by org' : `${questions.length} question${questions.length!==1?'s':''} added`}
                    {selectedQIds.size > 0 && (
                      <button className="btn btn-danger btn-sm" onClick={handleDeleteSelectedQs}>
                        🗑 Delete {selectedQIds.size} Selected
                      </button>
                    )}
                  </div>
                  {canEdit && (
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowQImport(true)}>📥 Import</button>
                      <button className="btn btn-primary btn-sm" onClick={() => { setEditQ({...EMPTY_Q}); setShowQModal(true); }}>+ Add Question</button>
                    </div>
                  )}
                </div>

                {questions === null && (
                  <div style={{ textAlign:'center', padding:32 }}>
                    <div style={{ fontSize:28, marginBottom:12 }}>🔒</div>
                    <div style={{ fontWeight:600, marginBottom:8 }}>Questions Hidden</div>
                    <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                      The organization has hidden questions and answers for this exam.<br/>
                      Contact the organization admin to request access.
                    </div>
                  </div>
                )}
                {questions !== null && questions.length === 0 && (
                  <div style={{ textAlign:'center', color:'var(--text-secondary)', padding:32 }}>No questions yet. Add your first question!</div>
                )}

              {questions !== null && questions.map((q, i) => {
                const optImgs = getOptionImages(q);
                const opts    = q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : [];
                return (
                  <div key={q.id} style={{ padding:'14px 16px', background:'var(--bg-elevated)', borderRadius:10, border:'1px solid var(--border)', marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6 }}>Q{i+1}</div>
                        {/* Render rich HTML question text */}
                        <RichContent html={q.question_text} style={{ fontSize:14 }} />
                        <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap', alignItems:'center' }}>
                          <span className="badge badge-blue">{q.question_type}</span>
                          <span className="badge badge-purple">{q.marks} mark{q.marks!==1?'s':''}</span>
                          {q.correct_answer && (
                            <span style={{ fontSize:12, padding:'2px 10px', borderRadius:20, background:'rgba(16,185,129,0.12)', color:'var(--accent-success)', fontWeight:600, border:'1px solid rgba(16,185,129,0.3)' }}>
                              ✓ {q.correct_answer.slice(0,50)}
                            </span>
                          )}
                        </div>
                        {/* Option images preview */}
                        {optImgs.some(Boolean) && (
                          <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                            {optImgs.map((img, ii) => img ? (
                              <img key={ii} src={img} alt={`opt ${ii+1}`}
                                style={{ height:50, borderRadius:4, border:'1px solid var(--border)', objectFit:'cover' }} />
                            ) : null)}
                          </div>
                        )}
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setPreviewQ(q)}>👁</button>
                        {canEdit && (
                          <input type="checkbox"
                            checked={selectedQIds.has(q.id)}
                            onChange={e => {
                              const next = new Set(selectedQIds);
                              e.target.checked ? next.add(q.id) : next.delete(q.id);
                              setSelectedQIds(next);
                            }}
                            onClick={e => e.stopPropagation()}
                            style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--accent-primary)' }} />
                        )}
                        {canEdit && <button className="btn btn-danger btn-sm" onClick={() => handleDeleteQ(q.id)}>✕</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )} {/* end leftTab questions */}

              {/* ── PROCTORS TAB ─────────────────────────────────────────── */}
              {leftTab === 'proctors' && (
                <div>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>Proctors assigned to this exam</div>
                  {proctorList === null ? (
                    <div style={{ textAlign:'center', padding:24 }}><div className="spinner" /></div>
                  ) : proctorList.length === 0 ? (
                    <div style={{ padding:32, textAlign:'center', color:'var(--text-secondary)', fontSize:13 }}>
                      <div style={{ fontSize:32, marginBottom:10 }}>👨‍🏫</div>
                      <div style={{ fontWeight:600, marginBottom:6 }}>No proctors assigned</div>
                      {isOrg && <div>Go to OrgDashboard → Exams → Assign Proctor</div>}
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {proctorList.map(p => (
                        <div key={p.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                          <div style={{ width:36, height:36, borderRadius:18, background:'linear-gradient(135deg,var(--accent-primary),var(--accent-secondary))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:15, flexShrink:0 }}>
                            {p.full_name?.[0]?.toUpperCase()}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>{p.full_name}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{p.email}</div>
                          </div>
                          <span className="badge badge-green" style={{ fontSize:10 }}>✓ Assigned</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          )} {/* end selectedExam card panel */}

          </div> {/* end inner 2-col grid: exam-list + questions-panel */}
          </div> {/* end left column of outer grid */}

          {/* ── Right: Checklist Panel ───────────────────────────────────── */}
          {checklistExam && isOrg && (
            <div className="card" style={{ position:'sticky', top:20, maxHeight:'calc(100vh - 120px)', overflowY:'auto', padding:'14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:13 }}>✅ Readiness Checklist</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{checklistExam.title}</div>
                </div>
                <button onClick={() => setChecklistExam(null)}
                  style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', color:'var(--text-secondary)', fontSize:13, padding:'3px 8px' }}>
                  ✕ Close
                </button>
              </div>
              <ExamChecklist
                exam={checklistExam}
                readOnly={!isOrg}
                onAction={isOrg ? (section) => {
                  if (section === 'edit') { setEditExam({...checklistExam, disabled_violations: checklistExam.disabled_violations || []}); setShowExamModal(true); }
                  else if (section === 'questions') fetchQuestions(checklistExam);
                  else if (section === 'links') toast.info('Go to OrgDashboard → Registration Links');
                  else if (section === 'proctor') toast.info('Go to OrgDashboard → Assign Instructor');
                  setTimeout(() => _loadChecklistStats(checklistExam.id), 1500);
                } : null}
              />
              {!isOrg && (
                <div style={{ marginTop:10, fontSize:11, color:'var(--text-muted)', padding:'8px 10px', background:'var(--bg-elevated)', borderRadius:6, textAlign:'center' }}>
                  ℹ️ Contact your organization to complete pending tasks
                </div>
              )}
            </div>
          )}

        </div> {/* end outer checklist grid */}

      </main>
    </div>

    {/* ── All Modals — outside layout+main, inside <> fragment ──────────── */}

    {/* ── Exam Create/Edit Modal ───────────────────────────────────────── */}
        {showExamModal && (
          <div className="modal-overlay" style={{ alignItems:'flex-start', paddingTop:40 }}>
            <div className="modal" style={{ maxWidth:700, maxHeight:'90vh', overflowY:'auto' }}>
              <h3 className="modal-title">{editExam.id ? 'Edit Exam' : 'Create New Exam'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Title *</label>
                  <input className="form-input" value={editExam.title}
                    onChange={e => setEditExam(p => ({...p, title:e.target.value}))} placeholder="Exam title" />
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Description</label>
                  <textarea className="form-input form-textarea" style={{ minHeight:70 }}
                    value={editExam.description || ''}
                    onChange={e => setEditExam(p => ({...p, description:e.target.value}))} placeholder="Short description" />
                </div>
                {[
                  { label:'Duration (min)',   key:'duration_minutes'  },
                  { label:'Total Marks',      key:'total_marks'       },
                  { label:'Passing Marks',    key:'passing_marks'     },
                  { label:'Max Violations',   key:'max_warnings'      },
                  { label:'Min Submit (min)', key:'min_submit_minutes' },
                ].map(f => (
                  <div key={f.key} className="form-group">
                    <label className="form-label">{f.label}</label>
                    <input className="form-input" type="number"
                      value={editExam[f.key] ?? 0}
                      onChange={e => setEditExam(p => ({...p, [f.key]: parseInt(e.target.value)||0}))} />
                  </div>
                ))}

                {/* Exam Instructions — rich text editor (Hindi/English + formatting) */}
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Exam Instructions</label>
                  <RichEditor
                    value={editExam.instructions || ''}
                    onChange={html => setEditExam(p => ({...p, instructions: html}))}
                    placeholder="Enter instructions shown to students before they start the exam... (Hindi/English both supported)"
                    minHeight={140}
                  />
                </div>

                {/* Date/Time window — always interpreted as IST (India), regardless of server/browser timezone */}
                <div className="form-group">
                  <label className="form-label">📅 Exam Start Date & Time <span style={{ fontSize:11, fontWeight:700, color:'var(--accent-primary)' }}>(IST)</span> <span style={{ fontSize:11, color:'var(--text-muted)' }}>— optional, students cannot start before this</span></label>
                  <input className="form-input" type="datetime-local"
                    min={nowLocalInput()}
                    value={toLocalInput(editExam.start_time)}
                    onChange={e => setEditExam(p => ({...p, start_time: istInputToUTC(e.target.value)}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">⏰ Exam End Date & Time <span style={{ fontSize:11, fontWeight:700, color:'var(--accent-primary)' }}>(IST)</span> <span style={{ fontSize:11, color:'var(--text-muted)' }}>— optional, students cannot start after this</span></label>
                  <input className="form-input" type="datetime-local"
                    min={nowLocalInput()}
                    value={toLocalInput(editExam.end_time)}
                    onChange={e => setEditExam(p => ({...p, end_time: istInputToUTC(e.target.value)}))} />
                </div>

                {/* Toggles */}
                {[
                  { label:'AI Proctoring Enabled',  key:'proctoring_enabled'  },
                  { label:'Require Fullscreen',     key:'require_fullscreen'  },
                  { label:'Shuffle Questions',      key:'shuffle_questions'   },
                  { label:'Published',              key:'is_published'        },
                ].map(f => (
                  <div key={f.key} style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <input type="checkbox" id={`ex-${f.key}`} checked={!!editExam[f.key]}
                      onChange={e => setEditExam(p => ({...p, [f.key]: e.target.checked}))}
                      style={{ width:16, height:16, accentColor:'var(--accent-primary)', cursor:'pointer' }} />
                    <label htmlFor={`ex-${f.key}`} style={{ fontSize:13, cursor:'pointer' }}>{f.label}</label>
                  </div>
                ))}
              </div>

              {/* ── Violation Settings ──────────────────────────────────────── */}
              <div style={{ marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)' }}>
                <h4 style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>🚨 Violation Settings</h4>
                <p style={{ fontSize:11, color:'var(--text-muted)', marginBottom:14 }}>
                  Uncheck any type that should only show a warning to the student without
                  counting toward their violation limit or trust score.
                </p>
                {VIOLATION_TYPES.map(group => (
                  <div key={group.group} style={{ marginBottom:14 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--text-secondary)', marginBottom:7 }}>{group.group}</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
                      {group.items.map(v => {
                        const isCounted = !(editExam.disabled_violations || []).includes(v.id);
                        return (
                          <label key={v.id} style={{ display:'flex', alignItems:'flex-start', gap:7, cursor:'pointer', padding:'7px 9px', borderRadius:7, background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
                            <input type="checkbox" checked={isCounted}
                              onChange={e => setEditExam(p => {
                                const current = p.disabled_violations || [];
                                return {
                                  ...p,
                                  disabled_violations: e.target.checked
                                    ? current.filter(x => x !== v.id)
                                    : [...current, v.id],
                                };
                              })}
                              style={{ width:14, height:14, marginTop:2, accentColor:'var(--accent-primary)', cursor:'pointer', flexShrink:0 }} />
                            <div>
                              <div style={{ fontSize:12, fontWeight:600 }}>{v.label}</div>
                              <div style={{ fontSize:10, color:'var(--text-muted)' }}>{v.hint}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24 }}>
                <button className="btn btn-secondary" onClick={() => { setShowExamModal(false); setEditExam(EMPTY_EXAM); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveExam} disabled={saving}>{saving ? 'Saving...' : 'Save Exam'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Question modal ─────────────────────────────────────────────────── */}
        {showQModal && (
          <div className="modal-overlay" style={{ alignItems:'flex-start', paddingTop:24 }}>
            <div className="modal" style={{ maxWidth:740, maxHeight:'92vh', overflowY:'auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 className="modal-title" style={{ margin:0 }}>Add Question</h3>
                <select className="form-input form-select" style={{ width:'auto', padding:'6px 12px' }}
                  value={editQ.question_type}
                  onChange={e => setEditQ(p => ({...p, question_type:e.target.value, options:['','','',''], option_images:['','','',''], correct_answer:''}))}>
                  <option value="mcq">Multiple Choice (MCQ)</option>
                  <option value="true_false">True / False</option>
                  <option value="short_answer">Short Answer</option>
                  <option value="essay">Essay / Long Answer</option>
                </select>
              </div>

              {/* Question text — full rich editor */}
              <div className="form-group">
                <label className="form-label">Question Text * <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400 }}>— supports bold, math, code, images</span></label>
                <RichEditor
                  value={editQ.question_text}
                  onChange={html => setEditQ(p => ({...p, question_text: html}))}
                  placeholder="Enter your question here. Use the toolbar for math (∑), code, images..."
                  minHeight={150}
                />
              </div>

              {/* MCQ options */}
              {editQ.question_type === 'mcq' && (
                <div className="form-group">
                  <label className="form-label">Answer Options <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400 }}>— each option can have text + optional image</span></label>
                  {editQ.options.map((opt, i) => (
                    <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom:10, padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                      {/* Option letter badge */}
                      <div style={{ width:28, height:28, borderRadius:6, background:'var(--accent-primary)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, flexShrink:0, marginTop:4 }}>
                        {String.fromCharCode(65+i)}
                      </div>
                      <div style={{ flex:1 }}>
                        <input className="form-input" style={{ marginBottom:8 }}
                          value={opt}
                          onChange={e => { const o=[...editQ.options]; o[i]=e.target.value; setEditQ(p=>({...p, options:o})); }}
                          placeholder={`Option ${String.fromCharCode(65+i)} text`} />
                        {/* Option image */}
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <input type="file" accept="image/*"
                            ref={el => optImageRefs.current[i] = el}
                            style={{ display:'none' }}
                            onChange={e => handleOptionImage(i, e.target.files[0])} />
                          {editQ.option_images?.[i] ? (
                            <div style={{ position:'relative', display:'inline-block' }}>
                              <img src={editQ.option_images[i]} alt={`opt ${i+1}`}
                                style={{ height:60, borderRadius:6, objectFit:'cover', border:'1px solid var(--border)' }} />
                              <button onClick={() => { const imgs=[...(editQ.option_images||['','','',''])]; imgs[i]=''; setEditQ(p=>({...p,option_images:imgs})); }}
                                style={{ position:'absolute', top:-6, right:-6, width:18, height:18, borderRadius:'50%', background:'var(--accent-danger)', color:'#fff', border:'none', cursor:'pointer', fontSize:12, lineHeight:1 }}>✕</button>
                            </div>
                          ) : (
                            <button onClick={() => optImageRefs.current[i]?.click()}
                              style={{ fontSize:11, padding:'4px 10px', borderRadius:6, border:'1px dashed var(--border)', background:'transparent', color:'var(--text-muted)', cursor:'pointer' }}>
                              🖼 Add image
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Add option button */}
                  {editQ.options.length < 6 && (
                    <button onClick={() => setEditQ(p => ({ ...p, options:[...p.options,''], option_images:[...(p.option_images||[]),''] }))}
                      style={{ fontSize:12, padding:'6px 14px', borderRadius:6, border:'1px dashed var(--border)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', marginTop:4 }}>
                      + Add Option
                    </button>
                  )}
                </div>
              )}

              {/* Correct answer */}
              {(editQ.question_type==='mcq' || editQ.question_type==='true_false') && (
                <div className="form-group">
                  <label className="form-label">Correct Answer *</label>
                  {editQ.question_type === 'true_false' ? (
                    <select className="form-input form-select" value={editQ.correct_answer}
                      onChange={e => setEditQ(p => ({...p, correct_answer:e.target.value}))}>
                      <option value="">Select...</option>
                      <option value="True">True</option>
                      <option value="False">False</option>
                    </select>
                  ) : (
                    <select className="form-input form-select" value={editQ.correct_answer}
                      onChange={e => setEditQ(p => ({...p, correct_answer:e.target.value}))}>
                      <option value="">Select correct option...</option>
                      {editQ.options.filter(o=>o.trim()).map((opt,i) => <option key={i} value={opt}>{String.fromCharCode(65+i)}: {opt.slice(0,60)}</option>)}
                    </select>
                  )}
                </div>
              )}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                <div className="form-group">
                  <label className="form-label">Marks</label>
                  <input className="form-input" type="number" min="1" value={editQ.marks}
                    onChange={e => setEditQ(p => ({...p, marks: parseInt(e.target.value)||1}))} />
                </div>
              </div>

              {/* Explanation — rich editor */}
              <div className="form-group">
                <label className="form-label">Explanation / Solution <span style={{ fontSize:11, color:'var(--text-muted)', fontWeight:400 }}>optional — shown after submission</span></label>
                <RichEditor
                  value={typeof editQ.explanation === 'string' && editQ.explanation.startsWith('{') ? (()=>{try{return JSON.parse(editQ.explanation).text||''}catch{return ''}})() : (editQ.explanation||'')}
                  onChange={html => setEditQ(p => ({...p, explanation: html}))}
                  placeholder="Explain why this is correct..."
                  minHeight={100}
                />
              </div>

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button className="btn btn-secondary" onClick={() => { setShowQModal(false); setEditQ(EMPTY_Q); }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveQuestion} disabled={saving}>{saving ? 'Saving...' : 'Add Question'}</button>
              </div>
            </div>
              )} {/* end questions tab */}

              {/* ── PROCTORS TAB ───────────────────────────────────────── */}
              {leftTab === 'proctors' && (
                <div>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>
                    Proctors assigned to this exam
                  </div>
                  {proctorList === null ? (
                    <div style={{ textAlign:'center', padding:24 }}><div className="spinner" /></div>
                  ) : proctorList.length === 0 ? (
                    <div style={{ padding:32, textAlign:'center', color:'var(--text-secondary)', fontSize:13 }}>
                      <div style={{ fontSize:32, marginBottom:10 }}>👨‍🏫</div>
                      <div style={{ fontWeight:600, marginBottom:6 }}>No proctors assigned</div>
                      {isOrg && <div>Go to <strong>OrgDashboard → Exams → Assign Proctor</strong></div>}
                      {isInstructor && <div>Contact your organization to be assigned as proctor.</div>}
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {proctorList.map(p => (
                        <div key={p.id} style={{ display:'flex', gap:10, alignItems:'center', padding:'10px 12px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)' }}>
                          <div style={{ width:36, height:36, borderRadius:18, background:'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:15, flexShrink:0 }}>
                            {p.full_name?.[0]?.toUpperCase()}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>{p.full_name}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)' }}>{p.email}</div>
                          </div>
                          <span className="badge badge-green" style={{ fontSize:10 }}>✓ Assigned</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

    {/* ── Clone Exam Modal */}
    {showCloneModal && cloneSource && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:500 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <h3 className="modal-title" style={{ margin:0 }}>⎘ Clone Exam</h3>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>Source: {cloneSource.title}</div>
                </div>
                <button onClick={() => { setShowCloneModal(false); setCloneSource(null); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
              </div>
              <div className="alert alert-info" style={{ marginBottom:16, fontSize:12 }}>
                All {cloneSource.question_count} questions will be copied. Students, attempts, and results are NOT cloned.
              </div>
              <CloneExamForm
                source={cloneSource}
                onSubmit={handleClone}
                onCancel={() => { setShowCloneModal(false); setCloneSource(null); }}
              />
            </div>
          </div>
        )}

        {/* ── Bulk Operations Modal */}
        {showBulkModal && (
          <BulkOperationsModal
            exam={showBulkModal}
            onClose={() => setShowBulkModal(null)}
            onDone={() => { setShowBulkModal(null); fetchExams(); }}
          />
        )}

        {/* ── Exam Settings Modal */}
        {settingsExam && (
          <ExamSettingsModal
            exam={settingsExam}
            onClose={() => setSettingsExam(null)}
            onSaved={() => { fetchExams(); setSettingsExam(null); }}
          />
        )}

        {/* ── Question Import Modal */}
        {showQImport && selectedExam && (
          <ImportModal
            title={`Import Questions — ${selectedExam.title}`}
            templateFilename="questions_template.csv"
            columns={[
              { key:'question_text',  label:'Question Text',   required:true,  example:'What is O(log n)?',  example2:'TCP guarantees delivery.',    aliases:['question','Question'] },
              { key:'question_type',  label:'Question Type',   required:false, example:'mcq',                example2:'true_false',                   aliases:['type','Type'] },
              { key:'option_a',       label:'Option A',        required:false, example:'O(1)',               example2:'', aliases:['OptionA','option_1'] },
              { key:'option_b',       label:'Option B',        required:false, example:'O(log n)',           example2:'', aliases:['OptionB','option_2'] },
              { key:'option_c',       label:'Option C',        required:false, example:'O(n)',               example2:'', aliases:['OptionC','option_3'] },
              { key:'option_d',       label:'Option D',        required:false, example:'O(n²)',              example2:'', aliases:['OptionD','option_4'] },
              { key:'correct_answer', label:'Correct Answer',  required:true,  example:'O(log n)',           example2:'True',  aliases:['answer','Answer'] },
              { key:'marks',          label:'Marks',           required:false, example:'2',                  example2:'2',     aliases:['Marks'] },
              { key:'explanation',    label:'Explanation',     required:false, example:'Binary search halves the search space each step.', example2:'' },
            ]}
            onImport={handleImportQuestions}
            onClose={() => setShowQImport(false)}
          />
        )}

        {/* ── Attendance Modal ─────────────────────────────────────────────── */}
        {showAttend && attendance && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:680, maxHeight:'85vh', overflowY:'auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <h3 className="modal-title" style={{ margin:0 }}>👥 Attendance — {attendance.exam?.title}</h3>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>Pre-exam monitoring</div>
                </div>
                <button onClick={() => setShowAttend(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
              </div>

              {/* Summary stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
                {[
                  { label:'Registered',    val:attendance.total_registered,   color:'var(--text-primary)' },
                  { label:'Logged In',     val:attendance.logged_in,          color:'var(--accent-success)' },
                  { label:'Absent',        val:attendance.absent,             color:'var(--accent-danger)' },
                  { label:'Instructions',  val:attendance.instructions_opened, color:'var(--accent-warning)' },
                  { label:'Started Exam', val:attendance.started_exam,        color:'var(--accent-primary)' },
                ].map(s => (
                  <div key={s.label} style={{ padding:'12px 14px', background:'var(--bg-elevated)', borderRadius:8, border:'1px solid var(--border)', textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:700, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Proctor publish button */}
              {(canEdit || isInstructor) && (
                <div style={{ padding:'12px 16px', background:'rgba(79,142,247,0.08)', borderRadius:8, border:'1px solid rgba(79,142,247,0.2)', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>Exam Status for Students</div>
                    <div style={{ fontSize:12, color:'var(--text-muted)' }}>{attendance.exam?.proctor_published ? 'Students can currently start this exam' : 'Students cannot start this exam yet'}</div>
                  </div>
                  <button className={`btn btn-sm ${attendance.exam?.proctor_published ? 'btn-danger' : 'btn-success'}`}
                    onClick={async () => {
                      if (attendance.exam?.proctor_published) {
                        if (window.confirm('Close this exam?')) {
                          await handleProctorPublish(attendance.exam, false, null);
                          setShowAttend(false);
                        }
                      } else {
                        const promptMsg = isInstructor
                          ? `🔑 Enter the exam password provided by your organization:`
                          : `🔑 Enter the exam password to open it to students:`;
                        const entered = window.prompt(promptMsg);
                        if (!entered) return;
                        await handleProctorPublish(attendance.exam, true, entered);
                        setShowAttend(false);
                      }
                    }}>
                    {attendance.exam?.proctor_published ? '🔴 Close Exam' : '🟢 Open to Students'}
                  </button>
                </div>
              )}

              {/* Student list */}
              <div className="table-container" style={{ border:'none' }}>
                <table>
                  <thead>
                    <tr><th>Student</th><th>Email</th><th>Logged In</th><th>Instructions</th><th>Exam Status</th></tr>
                  </thead>
                  <tbody>
                    {(attendance.students || []).map(s => (
                      <tr key={s.id}>
                        <td style={{ fontWeight:500 }}>{s.full_name}</td>
                        <td style={{ fontSize:12, color:'var(--text-muted)' }}>{s.email}</td>
                        <td>
                          {s.logged_in_at
                            ? <span style={{ color:'var(--accent-success)', fontSize:12 }}>✓ {new Date(s.logged_in_at).toLocaleTimeString()}</span>
                            : <span style={{ color:'var(--accent-danger)', fontSize:12 }}>✗ Absent</span>}
                        </td>
                        <td>
                          {s.instructions_at
                            ? <span style={{ color:'var(--accent-warning)', fontSize:12 }}>✓ Opened</span>
                            : <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>}
                        </td>
                        <td>
                          <span className={`badge badge-${s.attempt_status==='submitted'?'green':s.attempt_status==='in_progress'?'blue':s.attempt_status==='terminated'?'red':'yellow'}`}>
                            {s.attempt_status || 'not started'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Question preview modal ────────────────────────────────────────── */}
        {previewQ && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:640 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 className="modal-title" style={{ margin:0 }}>Question Preview</h3>
                <button onClick={() => setPreviewQ(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:20 }}>✕</button>
              </div>
              <div style={{ padding:20, background:'var(--bg-elevated)', borderRadius:10, border:'1px solid var(--border)', marginBottom:16 }}>
                <RichContent html={previewQ.question_text} />
              </div>
              {previewQ.question_type === 'mcq' && (() => {
                const opts    = previewQ.options ? (typeof previewQ.options==='string' ? JSON.parse(previewQ.options) : previewQ.options) : [];
                const optImgs = getOptionImages(previewQ);
                return (
                  <div>
                    {opts.map((opt, i) => (
                      <div key={i} style={{
                        display:'flex', alignItems:'center', gap:12, padding:'10px 14px',
                        background: opt===previewQ.correct_answer ? 'rgba(16,185,129,0.12)' : 'var(--bg-elevated)',
                        border:`1px solid ${opt===previewQ.correct_answer ? 'var(--accent-success)' : 'var(--border)'}`,
                        borderRadius:8, marginBottom:8,
                      }}>
                        <div style={{ width:28, height:28, borderRadius:6, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center',
                          background: opt===previewQ.correct_answer ? 'var(--accent-success)' : 'var(--bg-card)', color: opt===previewQ.correct_answer ? '#fff' : 'var(--text-secondary)', fontSize:13 }}>
                          {String.fromCharCode(65+i)}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:14 }}>{opt}</div>
                          {optImgs[i] && <img src={optImgs[i]} alt="" style={{ maxHeight:80, borderRadius:6, marginTop:6, maxWidth:'100%' }} />}
                        </div>
                        {opt===previewQ.correct_answer && <span style={{ color:'var(--accent-success)', fontWeight:700 }}>✓ Correct</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {previewQ.question_type === 'true_false' && (
                <div style={{ display:'flex', gap:10 }}>
                  {['True','False'].map(v => (
                    <div key={v} style={{ flex:1, padding:'12px 16px', textAlign:'center', borderRadius:8,
                      background: v===previewQ.correct_answer ? 'rgba(16,185,129,0.12)' : 'var(--bg-elevated)',
                      border:`1px solid ${v===previewQ.correct_answer ? 'var(--accent-success)' : 'var(--border)'}`,
                      fontWeight: v===previewQ.correct_answer ? 700 : 400 }}>
                      {v} {v===previewQ.correct_answer && '✓'}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ marginTop:14, fontSize:12, color:'var(--text-muted)', display:'flex', gap:12 }}>
                <span className="badge badge-blue">{previewQ.question_type}</span>
                <span className="badge badge-purple">{previewQ.marks} marks</span>
              </div>
            </div>
          </div>
        )}
    </>
  );
}
